import type { Ratecard } from '@clockwise/clients/ratecards';
import { BILLING_MODELS, type BillingModel, type EngagementInput } from '@clockwise/clients/sows';
import { Plus, Trash2 } from 'lucide-react';
import { EngagementCard } from '@/components/custom/engagement-card';
import type { ProductDisplayLookup } from '@/components/custom/product-label';
import { Button } from '@/components/ui/button';
import { formatRate } from '@/lib/format-rate';

const SECTION_TITLES: Record<BillingModel, string> = {
  retainer: 'Retainers',
  tm: 'Time & materials',
  metrics: 'Metrics',
};

/**
 * A SOW's engagements, grouped by billing model. In amend mode the cards are
 * clickable (open the editor sheet), each gains a delete affordance, and each
 * section offers an add button.
 *
 * When the SOW has no T&M engagement, the selected rate card's rates are shown
 * read-only under "Global": that is exactly what the customer bills at until a
 * customized T&M engagement exists. We show EITHER the global or the
 * customized rates, never both.
 *
 * Pure presentational; the parent owns the data and what each click does.
 */
export function EngagementsSection({
  engagements,
  amending,
  productLookup,
  roleLookup,
  rateCard,
  customerCadenceLabel,
  onEngagementClick,
  onEngagementDelete,
  onAdd,
}: {
  /** Keyed by the caller's staged draft order; ids are absent on new ones. */
  engagements: EngagementInput[];
  /** When true, cards are interactive and add/delete buttons are shown. */
  amending: boolean;
  productLookup: ProductDisplayLookup;
  roleLookup: (id: string) => string;
  /** The SOW's selected rate card. Badged on T&M cards, and shown as the
   *  read-only "Global" rates when the SOW has no T&M engagement yet. */
  rateCard: Ratecard | null;
  customerCadenceLabel: string;
  onEngagementClick: (engagement: EngagementInput, index: number) => void;
  onEngagementDelete: (index: number) => void;
  onAdd: (model: BillingModel) => void;
}) {
  const rows = engagements.map((engagement, index) => ({ engagement, index }));

  return (
    <div className="space-y-5">
      {BILLING_MODELS.map((model) => {
        const forModel = rows.filter((r) => r.engagement.billingModel === model);
        return (
          <Section
            key={model}
            title={`${SECTION_TITLES[model]} (${forModel.length})`}
            actionLabel={SECTION_TITLES[model]}
            amending={amending}
            onAdd={() => onAdd(model)}
          >
            {forModel.length > 0 ? (
              <ul className="space-y-2">
                {forModel.map(({ engagement, index }) => (
                  // `items-start` pins the delete button to the top of the row
                  // so it stays aligned with the card header on tall cards
                  // (many roles) instead of floating in the vertical middle.
                  //
                  // Keyed on the engagement's own id where it has one; a new
                  // engagement has none until it is saved, so its position in
                  // the staged list is the only identity available.
                  <li key={engagement.id ?? `new-${index}`} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <EngagementCard
                        engagement={engagement}
                        interactive={amending}
                        onClick={amending ? () => onEngagementClick(engagement, index) : undefined}
                        productLookup={productLookup}
                        roleLookup={roleLookup}
                        rateCardName={rateCard?.name ?? null}
                        customerCadenceLabel={customerCadenceLabel}
                      />
                    </div>
                    {amending ? (
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        className="mt-1.5"
                        onClick={() => onEngagementDelete(index)}
                        aria-label={`Delete ${engagement.name}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : model === 'tm' && rateCard && rateCard.lines.length > 0 ? (
              <GlobalRatesCard rateCard={rateCard} roleLookup={roleLookup} />
            ) : (
              <EmptyHint label={SECTION_TITLES[model]} amending={amending} />
            )}
          </Section>
        );
      })}
    </div>
  );
}

/**
 * Read-only "Global" rates shown when the SOW has no T&M engagement — the
 * selected rate card's entries, which is what the customer bills at until a
 * customized engagement is added. Mirrors the T&M card's layout so toggling
 * between global and customized doesn't jump the page.
 */
function GlobalRatesCard({
  rateCard,
  roleLookup,
}: {
  rateCard: Ratecard;
  roleLookup: (id: string) => string;
}) {
  const count = rateCard.lines.length;
  return (
    <div className="rounded-md border px-3 py-2 text-sm">
      <p className="flex flex-wrap items-center gap-1.5 font-medium">
        T&amp;M
        <span className="text-xs font-normal text-muted-foreground">
          {count} {count === 1 ? 'role' : 'roles'}
        </span>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">
          Global · {rateCard.name}
        </span>
      </p>
      <ul className="mt-1 divide-y divide-border/60">
        {rateCard.lines.map((l) => (
          <li key={l.serviceRoleId} className="flex items-center justify-between gap-3 py-1">
            <span className="truncate text-muted-foreground">{roleLookup(l.serviceRoleId)}</span>
            <span className="shrink-0 tabular-nums">{formatRate(l.rate)}/h</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Section({
  title,
  actionLabel,
  amending,
  onAdd,
  children,
}: {
  title: string;
  actionLabel: string;
  amending: boolean;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {amending ? (
          <Button type="button" size="sm" variant="outline" onClick={onAdd}>
            <Plus className="size-4" /> {actionLabel}
          </Button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ label, amending }: { label: string; amending: boolean }) {
  return (
    <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
      {amending
        ? `No ${label.toLowerCase()} yet. Click ${label} to add one.`
        : `No ${label.toLowerCase()} on this SOW.`}
    </p>
  );
}
